import type { ButtonHTMLAttributes, ReactNode } from 'react';
import { Kbd } from './Kbd';

export interface SearchPillProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Placeholder text inside the pill. Default: `Search…`. */
  label?: string;
  /** Trailing keyboard hint. Default: `⌘K`. Pass `null` to hide. */
  kbd?: string | null;
  /** Leading icon (defaults to a magnifying-glass glyph). */
  icon?: ReactNode;
}

const DEFAULT_ICON = (
  <svg
    viewBox="0 0 16 16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.4"
    aria-hidden="true"
    focusable="false"
  >
    <circle cx="7" cy="7" r="4.5" />
    <line x1="10.5" y1="10.5" x2="13.5" y2="13.5" strokeLinecap="round" />
  </svg>
);

/**
 * 32px search button — leading icon + label + trailing `<Kbd>` hint. Matches
 * dashboard.html `.search-pill`. Behaves as a button (typically opens a
 * command-palette modal); style only here.
 */
export function SearchPill({
  label = 'Search…',
  kbd = '⌘K',
  icon = DEFAULT_ICON,
  className,
  type,
  ...rest
}: SearchPillProps) {
  const cls = ['pn-search-pill', className].filter(Boolean).join(' ');
  return (
    <button type={type ?? 'button'} className={cls} {...rest}>
      <span className="pn-search-pill-ico" aria-hidden="true">
        {icon}
      </span>
      <span className="pn-search-pill-label">{label}</span>
      {kbd ? <Kbd className="pn-search-pill-kbd">{kbd}</Kbd> : null}
    </button>
  );
}
